// marketplace/ProductGrid.jsx
// Owns all filter + pagination state.
// Passes categories down to both CategoriesRow and FilterDropdown.
// Single fetch for categories (with_product_image=true serves both consumers).

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Search, SlidersHorizontal, X, Package } from "lucide-react";
import { brand }          from "../shared/brand";
import { ProductCard }    from "./ProductCard";
import { FilterDropdown } from "./FilterDropdown";
import { CategoriesRow }  from "./CategoriesRow";

const API_URL = process.env.REACT_APP_API_URL;

export function ProductGrid() {
  const [products, setProducts]       = useState([]);
  const [pagination, setPagination]   = useState(null);
  const [categories, setCategories]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterOpen, setFilterOpen]   = useState(false);

  const debounceRef = useRef(null);
  const filterRef   = useRef(null);

  const [query, setQuery] = useState({
    search:           "",
    debouncedSearch:  "",
    selectedCategory: "",
    minPrice:         "",
    maxPrice:         "",
    entregaHoyOnly:   false,
    page:             1,
  });

  // Close filter panel on outside click
  useEffect(() => {
    function handler(e) {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    }
    if (filterOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [filterOpen]);

  // Fetch categories once — with_product_image=true so CategoriesRow
  // gets the image + min_price data it needs to render the circles.
  useEffect(() => {
    axios
      .get(`${API_URL}/api/marketplace/categories?with_product_image=true`)
      .then(res => setCategories(res.data || []))
      .catch(() => {});
  }, []);

  // THE ONLY product fetch — fires whenever `query` changes
  useEffect(() => {
    let cancelled = false;
    const isFirst = query.page === 1;

    isFirst ? setLoading(true) : setLoadingMore(true);

    const params = { page: query.page, per_page: 24 };
    if (query.debouncedSearch)  params.q          = query.debouncedSearch;
    if (query.selectedCategory) params.category   = query.selectedCategory;
    if (query.minPrice)         params.min_price  = query.minPrice;
    if (query.maxPrice)         params.max_price  = query.maxPrice;
    if (query.entregaHoyOnly)   params.in_fastmax = true;

    axios
      .get(`${API_URL}/api/marketplace/products`, { params })
      .then(res => {
        if (cancelled) return;
        const incoming = res.data.products || [];
        setProducts(prev => {
          if (isFirst) {
            const seen = new Set();
            return incoming.filter(p => seen.has(p.id) ? false : seen.add(p.id));
          }
          const existingIds = new Set(prev.map(p => p.id));
          return [...prev, ...incoming.filter(p => !existingIds.has(p.id))];
        });
        setPagination(res.data.pagination);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) { setLoading(false); setLoadingMore(false); }
      });

    return () => { cancelled = true; };
  }, [query]);

  // ── Helpers — all update query atomically ────────────────────────────────

  function handleSearchChange(value) {
    setQuery(q => ({ ...q, search: value }));
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setQuery(q => ({ ...q, debouncedSearch: value, page: 1 }));
    }, 400);
  }

  function setCategory(val)   { setQuery(q => ({ ...q, selectedCategory: val, page: 1 })); }
  function setMinPrice(val)   { setQuery(q => ({ ...q, minPrice: val,          page: 1 })); }
  function setMaxPrice(val)   { setQuery(q => ({ ...q, maxPrice: val,          page: 1 })); }
  function toggleEntregaHoy() { setQuery(q => ({ ...q, entregaHoyOnly: !q.entregaHoyOnly, page: 1 })); }
  function loadNextPage()     { setQuery(q => ({ ...q, page: q.page + 1 })); }
  function clearFilters() {
    setQuery(q => ({ ...q, selectedCategory: "", minPrice: "", maxPrice: "", entregaHoyOnly: false, page: 1 }));
  }

  const activeFilters = [
    query.selectedCategory, query.minPrice, query.maxPrice, query.entregaHoyOnly,
  ].filter(Boolean).length;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      {/* CategoriesRow sits above the grid, receives state from here — no own fetch */}
      <CategoriesRow
        categories={categories}
        selectedCategory={query.selectedCategory}
        setCategory={setCategory}
      />

      <div className="mx-auto max-w-7xl px-3 py-4">

        {/* Search bar + Filter button */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
            <Search className="size-4 text-slate-400 shrink-0" />
            <input
              value={query.search}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder="Buscar productos..."
              className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
            />
            {query.search && (
              <button onClick={() => handleSearchChange("")}>
                <X className="size-4 text-slate-400 hover:text-slate-600" />
              </button>
            )}
          </div>

          <div className="relative" ref={filterRef}>
            <button
              onClick={() => setFilterOpen(o => !o)}
              className="relative flex items-center gap-1.5 rounded-xl border px-3 py-2.5 text-sm font-semibold bg-white shadow-sm transition-colors"
              style={{
                borderColor: activeFilters > 0 ? brand.primary : "#e2e8f0",
                color:       activeFilters > 0 ? brand.primary : "#374151",
              }}
            >
              <SlidersHorizontal className="size-4" />
              <span className="hidden sm:inline">Filtros</span>
              {activeFilters > 0 && (
                <span
                  className="absolute -top-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full text-[10px] font-bold text-white"
                  style={{ background: brand.primary }}
                >
                  {activeFilters}
                </span>
              )}
            </button>

            {filterOpen && (
              <FilterDropdown
                categories={categories}
                query={query}
                activeFilters={activeFilters}
                setCategory={setCategory}
                setMinPrice={setMinPrice}
                setMaxPrice={setMaxPrice}
                toggleEntregaHoy={toggleEntregaHoy}
                clearFilters={clearFilters}
              />
            )}
          </div>
        </div>

        {/* Active filter chips */}
        {activeFilters > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {query.selectedCategory && (
              <FilterChip
                label={categories.find(c => c.slug === query.selectedCategory)?.name || query.selectedCategory}
                onRemove={() => setCategory("")}
              />
            )}
            {(query.minPrice || query.maxPrice) && (
              <FilterChip
                label={`S/ ${query.minPrice || "0"} – ${query.maxPrice || "∞"}`}
                onRemove={() => setQuery(q => ({ ...q, minPrice: "", maxPrice: "", page: 1 }))}
              />
            )}
            {query.entregaHoyOnly && (
              <FilterChip
                label="Entrega hoy"
                onRemove={() => setQuery(q => ({ ...q, entregaHoyOnly: false, page: 1 }))}
              />
            )}
          </div>
        )}

        {/* Results count */}
        {!loading && pagination && (
          <p className="text-xs text-slate-400 mb-3">
            {pagination.total} producto{pagination.total !== 1 ? "s" : ""} encontrado{pagination.total !== 1 ? "s" : ""}
          </p>
        )}

        {/* Grid */}
        {loading ? (
          <GridSkeleton />
        ) : products.length === 0 ? (
          <EmptyState hasFilters={activeFilters > 0} onClear={clearFilters} />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {products.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {pagination?.has_next && (
              <div className="mt-6 flex justify-center">
                <button
                  onClick={loadNextPage}
                  disabled={loadingMore}
                  className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  {loadingMore
                    ? <><span className="size-4 rounded-full border-2 border-slate-300 border-t-slate-600 animate-spin" /> Cargando...</>
                    : "Ver más productos"
                  }
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Local sub-components ──────────────────────────────────────────────────────

function FilterChip({ label, onRemove }) {
  return (
    <div
      className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold text-white"
      style={{ background: brand.primary }}
    >
      {label}
      <button onClick={onRemove} aria-label="Quitar filtro">
        <X className="size-3" />
      </button>
    </div>
  );
}

function GridSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="rounded-2xl bg-white border border-slate-100 overflow-hidden">
          <div className="aspect-square bg-slate-100 animate-pulse" />
          <div className="p-2.5 space-y-2">
            <div className="h-2.5 w-1/2 rounded bg-slate-100 animate-pulse" />
            <div className="h-3   w-full rounded bg-slate-100 animate-pulse" />
            <div className="h-3.5 w-1/3 rounded bg-slate-200 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ hasFilters, onClear }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <Package className="size-12 text-slate-200 mb-4" />
      <p className="text-slate-600 font-semibold mb-1">No se encontraron productos</p>
      <p className="text-sm text-slate-400 mb-4">
        {hasFilters ? "Prueba ajustando los filtros" : "Vuelve pronto, estamos creciendo"}
      </p>
      {hasFilters && (
        <button
          onClick={onClear}
          className="rounded-xl px-4 py-2 text-sm font-semibold text-white"
          style={{ background: brand.primary }}
        >
          Limpiar filtros
        </button>
      )}
    </div>
  );
}